// MIDAS V3 — Composer (Phase 2 + Phase 7 attach port)
//
// Card-style composer per spec §7 + handoff chat-window.jsx (lines 774-799):
//   • max-width 820, centered
//   • padding 8×12, radius 12, var(--shadow)
//   • flex row align-items: flex-end
//   • MachinePill left (bottom-aligned with paddingBottom 4)
//   • AutoResizeTextarea grows 1→6 lines, then internal scroll
//   • Image preview chip row above textarea (when attached)
//   • `+` attach button 30×30 — NOW WIRED (Phase 7 port from V2):
//       - Disabled when no machineContext (mirrors V2 `imageUploadDisabled`)
//       - Opens hidden <input type="file"> on click
//       - Resizes image (max 2048px, JPEG q=0.85) before dispatching
//       - Image rides along on the existing sendMessage thunk `image?: File`
//   • `↑` primary send button 34×30
//   • Bottom hint row (mono 10.5, faint): `{machine} · ↵ send · ⇧↵ newline`
//     left + "Midas can make mistakes — verify critical values." right
//
// Dispatches `sendMessage` thunk from shared chatBotSlice. The thunk already
// accepts `image?: File` (used by V2) — no backwards-compatible extension
// needed, no new thunk added.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  clearError,
  sendMessage,
  setNewThreadPending,
} from "../../../../shared/chatBot/chatBotSlice";
import useTranslation from "../../../../shared/hooks/useTranslation";
import AutoResizeTextareaV3, {
  AutoResizeTextareaV3Handle,
} from "./AutoResizeTextareaV3";
import MachinePillV3 from "./MachinePillV3";
import { MIDAS_V3_FOCUS_COMPOSER_EVENT } from "./artifacts/FullscreenArtifactV3";
import {
  ACCEPTED_IMAGE_EXTENSIONS,
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_BYTES,
  resizeImageForUpload,
} from "./v3-utils/resizeImageForUpload";

const MAX_CHARS = 5000;

const ChatInputAreaV3: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const isBotAvailable = useAppSelector((s) => s.chatBot.isBotAvailable);

  // Thread-scoped streaming flag (matches v2 selector pattern). When the
  // active thread is streaming we disable the send button so a second message
  // can't queue mid-stream.
  const activeThreadIsStreaming = useAppSelector((s) => {
    const tid = s.midasPage.activeThreadId;
    if (!tid) return s.chatBot.isLoading;
    return s.chatBot.threadBuffers[tid]?.isLoading || false;
  });

  const [value, setValue] = useState("");
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [snackError, setSnackError] = useState<string | null>(null);
  const textareaRef = useRef<AutoResizeTextareaV3Handle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const disabled = isBotAvailable === false || activeThreadIsStreaming;
  // Mirrors V2 MidasChatAreaV2 line 267: `imageUploadDisabled={!machineContext}`.
  // No machine in context → bot has nowhere to attach the image, so we disable.
  const imageUploadDisabled = !machineContext;

  // MIDAS-V3-P3: Listen for "focus composer with prefilled text" events
  // dispatched by FullscreenArtifactV3 when the user clicks "+ Spýtať sa
  // MIDAS". Loose coupling via CustomEvent so neither component imports the
  // other's internals.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ text: string }>).detail;
      if (!detail || typeof detail.text !== "string") return;
      setValue((current) => {
        // Only overwrite if empty so we don't clobber an in-progress draft.
        if (current && current.length > 0) return current;
        return detail.text;
      });
      // Focus on the next frame so the underlying textarea is laid out.
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    };
    window.addEventListener(MIDAS_V3_FOCUS_COMPOSER_EVENT, handler);
    return () =>
      window.removeEventListener(MIDAS_V3_FOCUS_COMPOSER_EVENT, handler);
  }, []);

  // Generate preview URL for the attached image. Revoke on replace / unmount
  // so we don't leak blob URLs. Mirrors V2 useEffect behaviour.
  useEffect(() => {
    if (!attachedImage) {
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachedImage);
    setImagePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [attachedImage]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    // Allow image-only sends (empty text + attached image). Matches V2.
    if (!trimmed && !attachedImage) return;
    if (disabled) return;

    dispatch(clearError());
    if (!activeThreadId) {
      dispatch(setNewThreadPending());
    }
    dispatch(
      sendMessage({
        message: trimmed,
        machineId: machineContext?.machineId,
        threadId: activeThreadId || undefined,
        image: attachedImage || undefined,
        originThreadId: activeThreadId || undefined,
      })
    );

    setValue("");
    setAttachedImage(null);
    // Reset the textarea height after React flushes the state.
    requestAnimationFrame(() => {
      textareaRef.current?.reset();
    });
  }, [dispatch, value, disabled, activeThreadId, machineContext, attachedImage]);

  const handleAttachClick = useCallback(() => {
    if (disabled || imageUploadDisabled) return;
    fileInputRef.current?.click();
  }, [disabled, imageUploadDisabled]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so the same file can be re-selected after a remove.
      e.target.value = "";
      if (!file) return;

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setSnackError(
          translate("midas.imageUpload.unsupportedFormat") as string
        );
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setSnackError(translate("midas.imageUpload.imageTooLarge") as string);
        return;
      }

      const resized = await resizeImageForUpload(file);
      setAttachedImage(resized);
    },
    [translate]
  );

  const handleRemoveImage = useCallback(() => {
    setAttachedImage(null);
  }, []);

  // Build the scope label (machine model + brand or platform).
  const machineName = (() => {
    if (!machineContext) return "";
    const model = machineContext.model?.trim() || "";
    const brand = machineContext.manufacturerName?.trim() || "";
    if (model) return brand ? `${brand} ${model}` : model;
    if (machineContext.internalNumber) return machineContext.internalNumber;
    if (machineContext.serialNumber) return `SN ${machineContext.serialNumber}`;
    return "";
  })();

  const placeholder = translate("midas.v3.composer.placeholder", {
    defaultValue: "Ask Midas anything…",
  }) as string;

  const sendHint = translate("midas.v3.composer.sendHint", {
    defaultValue: "↵ send · ⇧↵ newline",
  }) as string;

  const disclaimer = translate("midas.v3.composer.disclaimer", {
    defaultValue: "Midas can make mistakes — verify critical values.",
  }) as string;

  const attachLabel = imageUploadDisabled
    ? (translate("midas.imageUpload.selectMachineHint") as string)
    : (translate("midas.imageUpload.attachImage") as string);

  const canSend =
    (value.trim().length > 0 || attachedImage !== null) && !disabled;

  return (
    <div
      style={{
        borderTop: "1px solid var(--border)",
        padding: "14px 32px",
        background: "var(--bg)",
      }}
    >
      {/* Hidden file input — no `capture` attribute (BUG-MIDAS-MOBILE-0414) */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_EXTENSIONS}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "8px 12px",
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "var(--shadow)",
        }}
      >
        {/* Image preview chip (only when attached) */}
        {attachedImage && imagePreviewUrl && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "2px 0",
            }}
          >
            <div
              style={{
                position: "relative",
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid var(--border)",
                background: "var(--card-2)",
              }}
            >
              <img
                src={imagePreviewUrl}
                alt={attachedImage.name}
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                aria-label={
                  translate("midas.v3.composer.removeAttachmentAria", {
                    defaultValue: "Remove attached image",
                  }) as string
                }
                title={
                  translate("midas.v3.composer.removeAttachmentAria", {
                    defaultValue: "Remove attached image",
                  }) as string
                }
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 18,
                  height: 18,
                  padding: 0,
                  border: "none",
                  borderRadius: 9,
                  background: "rgba(0,0,0,0.7)",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                  fontSize: 11,
                  lineHeight: 1,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                ×
              </button>
            </div>
            <span
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                color: "var(--muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 240,
              }}
            >
              {attachedImage.name}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 8,
          }}
        >
          <div style={{ paddingBottom: 4, flexShrink: 0 }}>
            <MachinePillV3 compact />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <AutoResizeTextareaV3
              ref={textareaRef}
              value={value}
              onChange={setValue}
              onSubmit={handleSubmit}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={MAX_CHARS}
              ariaLabel={placeholder}
            />
          </div>
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={disabled || imageUploadDisabled}
            aria-label={attachLabel}
            title={attachLabel}
            style={{
              width: 30,
              height: 30,
              padding: 0,
              flexShrink: 0,
              background: "transparent",
              color:
                disabled || imageUploadDisabled
                  ? "var(--faint)"
                  : "var(--muted)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              cursor:
                disabled || imageUploadDisabled ? "not-allowed" : "pointer",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              opacity: disabled || imageUploadDisabled ? 0.5 : 1,
              transition:
                "background 0.12s ease, color 0.12s ease, border-color 0.12s ease, opacity 0.12s ease",
            }}
          >
            ＋
          </button>
          <button
            type="button"
            onClick={canSend ? handleSubmit : undefined}
            disabled={!canSend}
            aria-label={
              translate("midas.v3.composer.sendAria", {
                defaultValue: "Send message",
              }) as string
            }
            style={{
              width: 34,
              height: 30,
              padding: 0,
              flexShrink: 0,
              background: canSend ? "var(--accent)" : "var(--card-2)",
              color: canSend ? "white" : "var(--faint)",
              border:
                "1px solid " + (canSend ? "var(--accent)" : "var(--border)"),
              borderRadius: 8,
              cursor: canSend ? "pointer" : "not-allowed",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              transition:
                "background 0.12s ease, color 0.12s ease, border-color 0.12s ease",
            }}
          >
            ↑
          </button>
        </div>
      </div>
      <div
        style={{
          maxWidth: 820,
          margin: "6px auto 0",
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          fontSize: 10.5,
          color: "var(--faint)",
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.04em",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }}
        >
          {machineName ? `${machineName} · ${sendHint}` : sendHint}
        </span>
        <span style={{ textAlign: "right" }}>{disclaimer}</span>
      </div>

      {/* Error toast — plain HTML, no MUI Snackbar. Same UX as V2. */}
      {snackError && (
        <div
          role="alert"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #FCA5A5",
            background: "#FEF2F2",
            color: "#B91C1C",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>{snackError}</span>
          <button
            type="button"
            onClick={() => setSnackError(null)}
            aria-label={
              translate("midas.v3.composer.dismissErrorAria", {
                defaultValue: "Dismiss",
              }) as string
            }
            style={{
              border: "none",
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              textTransform: "uppercase",
              opacity: 0.7,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatInputAreaV3;
